import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ReferenceLine, YAxis } from './Charts/RechartsComponents';
import { Card, Typography, useTheme, Paper, Divider, Box, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

// Custom colors for different data series
const COLORS = [
  '#ff9800', // Orange (primary - average)
  '#4caf50', // Green (minimum)
  '#f44336', // Red (maximum)
  '#3f51b5', // Indigo
  '#9c27b0', // Purple
  '#00bcd4', // Cyan
  '#8bc34a', // Light Green
  '#ffc107', // Amber
  '#009688', // Teal
  '#673ab7', // Deep Purple
  '#03a9f4', // Light Blue
  '#e91e63', // Pink
];

const CustomTooltip = ({ active, payload, label, isCategorical }) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    // If this is categorical data represented as time series
    if (isCategorical) {
      return (
        <Paper
          elevation={3}
          sx={{
            padding: '12px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ccc',
            borderRadius: '8px',
            maxWidth: '300px',
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            {new Date(label).toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'})}
          </Typography>
          <Divider sx={{ my: 1 }} />
          
          {payload.map((entry, index) => {
            // Skip the "name" entries which are just for reference
            if (entry.dataKey.endsWith('_name')) return null;
            
            // Find the original category name if available
            const nameKey = `${entry.dataKey}_name`;
            const categoryName = payload.find(p => p.dataKey === nameKey)?.value || entry.dataKey;
            
            return (
              <Typography 
                key={index} 
                variant="body2" 
                sx={{ 
                  mb: 0.5, 
                  display: 'flex', 
                  justifyContent: 'space-between' 
                }}
              >
                <span>{categoryName}:</span> 
                <span style={{ fontWeight: 'bold', color: entry.color }}>
                  {entry.value}
                </span>
              </Typography>
            );
          })}
        </Paper>
      );
    }
    
    // Default tooltip for numerical data
    const { average, minimum, maximum, sum, documentNames } = payload[0].payload;
    return (
      <Paper
        elevation={3}
        sx={{
          padding: '12px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          borderRadius: '8px',
          maxWidth: '300px',
          boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
          {new Date(label).toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'})}
        </Typography>
        <Divider sx={{ my: 1 }} />
        <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
          <span>Average:</span> <span style={{ fontWeight: 'bold', color: '#ff9800' }}>{average.toFixed(2)}</span>
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
          <span>Minimum:</span> <span style={{ fontWeight: 'bold', color: '#4caf50' }}>{minimum.toFixed(2)}</span>
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
          <span>Maximum:</span> <span style={{ fontWeight: 'bold', color: '#f44336' }}>{maximum.toFixed(2)}</span>
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
          <span>Sum:</span> <span style={{ fontWeight: 'bold', color: '#3f51b5' }}>{sum.toFixed(2)}</span>
        </Typography>
        
        {documentNames && documentNames.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" fontWeight="medium">
              Documents:
            </Typography>
            <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
              {documentNames.map((name, index) => (
                <Typography 
                  key={index} 
                  variant="caption" 
                  sx={{ 
                    display: 'block',
                    ml: 1,
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  • {name.length > 50 ? '...' + name.slice(-40) : name}
                </Typography>
              ))}
            </div>
          </>
        )}
      </Paper>
    );
  }

  return null;
};

const LineChartComponent = ({ data = [], timeSeriesData = [], tooltipFormatter, dataType = 'numerical' }) => {
  const theme = useTheme();
  const isCategorical = dataType === 'categorical';

  // For categorical data, provide a way to select which lines to show (limit to avoid visual clutter)
  const [selectedCategories, setSelectedCategories] = useState([]);

  // Determine which dataset to use
  const chartData = isCategorical && timeSeriesData.length > 0 ? timeSeriesData : data;
  
  // If this is categorical data, extract the categories
  const categories = useMemo(() => {
    if (!isCategorical || !timeSeriesData.length) return [];
    
    // Create a set of all unique category keys (excluding date and *_name keys)
    const categoryKeys = new Set();
    timeSeriesData.forEach(dataPoint => {
      Object.keys(dataPoint).forEach(key => {
        if (key !== 'date' && !key.endsWith('_name')) {
          categoryKeys.add(key);
        }
      });
    });
    
    return Array.from(categoryKeys);
  }, [isCategorical, timeSeriesData]);

  // Keep track of available numeric fields
  const fieldsToShow = isCategorical 
    ? (selectedCategories.length > 0 ? selectedCategories : categories.slice(0, 5)) // Show top 5 categories by default
    : ['average', 'minimum', 'maximum'];
  
  // Calculate statistics for numerical data
  const totalItems = data.length;
  const sumValues = data.reduce((acc, item) => acc + (item.average || 0), 0);
  const avgValue = totalItems > 0 ? sumValues / totalItems : 0;

  // Handle category selection
  const handleCategoryChange = (event) => {
    setSelectedCategories(event.target.value);
  };

  return (
    <Card
      sx={{ 
        boxShadow: '0 6px 10px rgba(0,0,0,0.15)', 
        borderRadius: 2,
        p: 2, 
        backgroundColor: 'white',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
        }
      }}
    >
      {/* Category selector for categorical data */}
      {isCategorical && categories.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="category-select-label">Categories to Display</InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              multiple
              value={selectedCategories}
              onChange={handleCategoryChange}
              renderValue={(selected) => selected.join(', ')}
              label="Categories to Display"
            >
              {categories.map((category, index) => (
                <MenuItem key={category} value={category}>
                  {/* Try to find the original category name */}
                  {timeSeriesData[0] && timeSeriesData[0][`${category}_name`] || category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      
      {/* Chart Container */}
      <ResponsiveContainer width="100%" height={400}>
        <RechartsLineChart
          data={chartData}
          margin={{
            top: 20, right: 30, left: 10, bottom: 10,
          }}
        >
          {/* Add gradients for line chart fills */}
          <defs>
            {COLORS.map((color, index) => (
              <linearGradient key={`gradient-${index}`} id={`color${index}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                <stop offset="95%" stopColor={color} stopOpacity={0.1}/>
              </linearGradient>
            ))}
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="date" 
            tick={{ fill: '#666', fontSize: 12 }} 
            stroke="#aaa"
            tickFormatter={(date) => {
              try {
                return new Date(date).toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
              } catch (e) {
                return date;
              }
            }}
          />
          <YAxis 
            domain={['auto', 'auto']} 
            tick={{ fill: '#666', fontSize: 12 }} 
            stroke="#aaa"
          />
          <Tooltip 
            content={<CustomTooltip isCategorical={isCategorical} />} 
            cursor={{ stroke: '#3f51b5', strokeWidth: 1, strokeDasharray: '5 5' }}
          />
          <Legend 
            verticalAlign="top" 
            height={36} 
            iconType="circle"
            wrapperStyle={{ fontSize: '12px' }}
            formatter={(value, entry) => {
              // For categorical data, try to get the original category name
              if (isCategorical) {
                const nameKey = `${entry.dataKey}_name`;
                const categoryName = chartData[0]?.[nameKey] || entry.dataKey;
                return <span style={{ color: entry.color }}>{categoryName}</span>;
              }
              return <span style={{ color: entry.color }}>{value}</span>;
            }}
          />
          
          {/* If numerical, show average reference line */}
          {!isCategorical && (
            <ReferenceLine y={avgValue} stroke="#9c27b0" strokeDasharray="3 3" label={{ value: 'Avg', position: 'right', fill: '#9c27b0', fontSize: 12 }} />
          )}
          
          {/* Add appropriate lines based on data type */}
          {fieldsToShow.map((field, index) => (
            <Line
              key={field}
              type="monotone"
              dataKey={field}
              name={isCategorical ? field : field.charAt(0).toUpperCase() + field.slice(1)}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={2}
              dot={{ r: 2, stroke: COLORS[index % COLORS.length], strokeWidth: 2, fill: 'white' }}
              activeDot={{ r: 6, stroke: 'white', strokeWidth: 2, fill: COLORS[index % COLORS.length] }}
              animationDuration={1000 + (200 * index)}
              animationBegin={300 * index}
              isAnimationActive={true}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default LineChartComponent;